import type { JSX } from 'react';
import type { ClientResource } from '@voxler/contracts';
import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { ClientApiKeys } from '../components/ClientApiKeys';
import { ClientBillingInfo } from '../components/ClientBillingInfo';
import { ClientCapHistory } from '../components/ClientCapHistory';
import { ClientCaps } from '../components/ClientCaps';
import { ClientCharges } from '../components/ClientCharges';
import { ClientMandate } from '../components/ClientMandate';
import { ClientUseCases } from '../components/ClientUseCases';
import { ClientUsers } from '../components/ClientUsers';
import { ClientWebhook } from '../components/ClientWebhook';
import { billingInfoIncomplete } from '../components/client-helpers';
import { PageChrome } from '../components/page-chrome';
import { internalFetch } from '../lib/api';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/code';
import { LoadingState } from '@/components/ui/empty';
import { Split21 } from '@/components/ui/layout-grid';
import { PageHead } from '@/components/ui/page-header';
import { clientStatusLabel, StatusBadge } from '@/components/ui/status-tag';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

type DetailTab = 'billing' | 'access' | 'charges' | 'use-cases' | 'users' | 'history';

const DETAIL_TABS: readonly { value: DetailTab; label: string }[] = [
  { value: 'billing', label: 'Facturation' },
  { value: 'access', label: 'Accès API' },
  { value: 'charges', label: 'Abonnements et frais' },
  { value: 'use-cases', label: 'Cas d’usage' },
  { value: 'users', label: 'Utilisateurs' },
  { value: 'history', label: 'Historique des plafonds' },
];

/** Narrows a Radix tab value to a known tab of the client detail. */
function toDetailTab(value: string): DetailTab | undefined {
  return DETAIL_TABS.find((tab) => tab.value === value)?.value;
}

/** Tab panels stay mounted (forms keep their input); inactive ones are hidden. */
const PANEL_CLASS = 'grid gap-6';

export function ClientDetail({ clientId }: { clientId: string }): JSX.Element {
  const [tab, setTab] = useState<DetailTab>('billing');
  const [capsOpen, setCapsOpen] = useState(false);

  const clients = useQuery({
    queryKey: ['clients'],
    queryFn: () => internalFetch<{ clients: ClientResource[] }>('/internal/clients'),
  });
  const client = clients.data?.clients.find((candidate) => candidate.id === clientId);

  return (
    <>
      <PageChrome
        crumbs={[{ label: 'Clients', to: '/clients' }, { label: client?.name ?? 'Détail client' }]}
        actions={
          client !== undefined ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCapsOpen(true);
              }}
            >
              <Pencil aria-hidden="true" />
              Modifier les plafonds
            </Button>
          ) : undefined
        }
      />
      <PageHead
        title={client?.name ?? 'Détail client'}
        subtitle={client?.billingEmail}
        tools={
          <>
            <span className="font-mono text-xs text-muted-foreground">{clientId}</span>
            <CopyButton text={clientId} label="Copier l’identifiant du client" />
            {client !== undefined ? (
              <StatusBadge status={clientStatusLabel(client.status)} />
            ) : null}
          </>
        }
      />

      {client !== undefined && billingInfoIncomplete(client) ? (
        <Callout
          variant="warning"
          action={
            tab !== 'billing' ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setTab('billing');
                }}
              >
                Compléter
              </Button>
            ) : undefined
          }
        >
          Adresse de facturation et SIREN obligatoires : sans eux, la facture Qonto du mois ne
          pourra pas être émise.
        </Callout>
      ) : null}

      {clients.isLoading ? <LoadingState /> : null}

      {client !== undefined ? (
        <ClientCaps client={client} editOpen={capsOpen} onEditOpenChange={setCapsOpen} />
      ) : null}

      <Tabs
        value={tab}
        onValueChange={(next) => {
          const value = toDetailTab(next);
          if (value !== undefined) {
            setTab(value);
          }
        }}
        className="grid gap-4"
      >
        <TabsList aria-label="Sections de la fiche client" className="justify-self-start">
          {DETAIL_TABS.map((item) => (
            <TabsTrigger key={item.value} value={item.value}>
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="billing" keepMounted className={PANEL_CLASS}>
          <Split21 className="items-start">
            {client !== undefined ? <ClientBillingInfo key={client.id} client={client} /> : <div />}
            <ClientMandate clientId={clientId} />
          </Split21>
        </TabsContent>

        <TabsContent value="access" keepMounted className={PANEL_CLASS}>
          <Split21 className="items-start">
            <ClientApiKeys clientId={clientId} />
            <div className="grid gap-6">
              <ClientWebhook clientId={clientId} environment="production" />
              <ClientWebhook clientId={clientId} environment="staging" />
            </div>
          </Split21>
        </TabsContent>

        <TabsContent value="charges" keepMounted className={PANEL_CLASS}>
          <ClientCharges clientId={clientId} />
        </TabsContent>

        <TabsContent value="use-cases" keepMounted className={PANEL_CLASS}>
          <ClientUseCases clientId={clientId} />
        </TabsContent>

        <TabsContent value="users" keepMounted className={PANEL_CLASS}>
          <ClientUsers clientId={clientId} defaultUserCap={client?.defaultUserCap ?? null} />
        </TabsContent>

        <TabsContent value="history" keepMounted className={PANEL_CLASS}>
          <ClientCapHistory clientId={clientId} />
        </TabsContent>
      </Tabs>
    </>
  );
}
